import styled from 'styled-components';

export const TableAndFilterWrapper = styled.div`
  width: 100%;
  border-radius: var(--radius-2xl);
  border: 1px solid var(--color-gray-200);
  background-color: var(--color-white);
  padding: var(--spacing-6);
  box-shadow: var(--shadow-sm);
  overflow: hidden; /* Prevent content overflow */

  /* Responsive table handling */
  @media (max-width: 768px) {
    border-radius: var(--radius-md);
    margin: 0 -var(--spacing-2);
    border-left: none;
    border-right: none;
  }
`;

export const FilterAndPaginationWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
  margin-bottom: var(--spacing-6);
`;

// Table Container
export const StyledTableContainer = styled.div`
  width: 100%;
  // max-width: 1330px;
  border-radius: var(--radius-lg);
  // border: 1px solid var(--color-gray-200);
  background-color: var(--color-white);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  // overflow: auto;

  /* Responsive table handling */
  @media (max-width: 768px) {
    border-radius: var(--radius-md);
    margin: 0 -var(--spacing-2);
    border-left: none;
    border-right: none;
  }
`;

// Main Table
export const StyledTable = styled.table<{
  $size: 'sm' | 'md' | 'lg';
  $variant: string;
  $cols?: number;
  $hasCheckbox?: boolean;
}>`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: ${({ $size }) =>
    $size === 'lg' ? 'var(--font-size-16)' : 'var(--font-size-14)'};

  ${({ $variant }) =>
    $variant === 'bordered' &&
    `
    border: 1px solid var(--color-gray-200);
  `}

  ${({ $variant }) =>
    $variant === 'striped' &&
    `
    tbody tr:nth-child(even) {
      background-color: var(--color-gray-25);
    }
  `}

  /* Default column width distribution (without checkbox) */
  ${({ $hasCheckbox }) =>
    !$hasCheckbox &&
    `
    col:nth-child(1) {
      width: 40%;
    } /* Hotel */
    col:nth-child(2) {
      width: 15%;
    } /* Tier */
    col:nth-child(3) {
      width: 15%;
    } /* Score */
    col:nth-child(4) {
      width: 24%;
    } /* Status */
    col:nth-child(5) {
      width: 6%;
    } /* Action */
  `}

  /* Column width distribution with checkbox */
  ${({ $hasCheckbox }) =>
    $hasCheckbox &&
    `
    col:nth-child(1) {
      width: 5%;
    } /* Checkbox */
    col:nth-child(2) {
      width: 35%;
    } /* Hotel */
    col:nth-child(3) {
      width: 15%;
    } /* Tier */
    col:nth-child(4) {
      width: 15%;
    } /* Score */
    col:nth-child(5) {
      width: 24%;
    } /* Status */
    col:nth-child(6) {
      width: 6%;
    } /* Action or empty */
  `}

  @media (max-width: 768px) {
    table-layout: auto;
    font-size: var(--font-size-14);

    /* Without checkbox mobile */
    ${({ $hasCheckbox }) =>
      !$hasCheckbox &&
      `
      col:nth-child(1) {
        width: 40%;
      }
      col:nth-child(2) {
        width: 18%;
      }
      col:nth-child(3) {
        width: 18%;
      }
      col:nth-child(4) {
        width: 19%;
      }
      col:nth-child(5) {
        width: 5%;
      }
    `}

    /* With checkbox mobile */
    ${({ $hasCheckbox }) =>
      $hasCheckbox &&
      `
      col:nth-child(1) {
        width: 5%;
      }
      col:nth-child(2) {
        width: 35%;
      }
      col:nth-child(3) {
        width: 15%;
      }
      col:nth-child(4) {
        width: 15%;
      }
      col:nth-child(5) {
        width: 20%;
      }
      col:nth-child(6) {
        width: 10%;
      }
    `}
  }
`;

// Table Header
export const StyledTableHeader = styled.thead<{
  $appearance?: 'default' | 'minimal' | 'ghost';
}>`
  ${({ $appearance = 'default' }) => {
    switch ($appearance) {
      case 'minimal':
        return `
          background-color: var(--color-gray-50);
          // border-bottom: 1px solid var(--color-gray-200);
        `;
      case 'ghost':
        return `
          background-color: transparent;
          border-bottom: none;
        `;
      case 'default':
      default:
        return `
          background-color: var(--color-gray-50);
          // border-bottom: 1px solid var(--color-gray-200);
          border-bottom : none;
        `;
    }
  }}
`;

// Table Body
export const StyledTableBody = styled.tbody`
  background-color: var(--color-white);
`;

// Table Row
export const StyledTableRow = styled.tr<{
  $variant?: 'default' | 'active' | 'hover';
  $appearance?: 'default' | 'minimal' | 'ghost';
}>`
  transition: var(--transition-fast);
  // padding: var(--spacing-8);

  &:hover {
    // background-color: var(--color-gray-25);
  }

  ${({ $variant }) =>
    $variant === 'active' &&
    `
    background-color: var(--color-primary-50);
    
    &:hover {
      // background-color: var(--color-primary-100);
    }
  `}

  ${({ $variant }) =>
    $variant === 'hover' &&
    `
    &:hover {
      background-color: var(--color-primary-25);
    }
  `}

  ${({ $appearance = 'default' }) => {
    switch ($appearance) {
      case 'minimal':
        return `
          // border-bottom: 1px solid var(--color-gray-200);
          
          &:hover {
            background-color: transparent;
          }
        `;
      case 'ghost':
        return `
          border-bottom: none;
          
          &:hover {
            background-color: transparent;
          }
        `;
      case 'default':
      default:
        return '';
    }
  }}
`;

// Table Cell
export const StyledTableCell = styled.td<{
  $variant?: 'default' | 'header' | 'accent';
  $align?: 'left' | 'center' | 'right';
  $appearance?: 'default' | 'minimal' | 'ghost';
}>`
  padding: var(--spacing-5) var(--spacing-4);
  vertical-align: middle;
  text-align: ${({ $align }) => $align || 'left'};
  word-wrap: break-word;
  overflow-wrap: break-word;

  ${({ $variant }) =>
    $variant === 'header' &&
    `
    font-weight: var(--font-weight-semibold);
    color: var(--color-gray-900);
  `}

  ${({ $variant }) =>
    $variant === 'accent' &&
    `
    background-color: var(--color-primary-25);
    color: var(--color-primary-600);
  `}

  ${({ $appearance = 'default' }) => {
    switch ($appearance) {
      case 'minimal':
        return `border-bottom: 1px solid var(--color-gray-200);`;
      case 'ghost':
        return `border-bottom: none;`;
      case 'default':
      default:
        return `border-bottom: none`;
    }
  }}
  
  @media (max-width: 768px) {
    padding: var(--spacing-2) var(--spacing-3);

    /* Hide less important columns on mobile */
    &:nth-child(n + 5) {
      display: none;
    }
  }
`;

// Table Header Cell
export const StyledTableHeaderCell = styled.th<{
  $sortable?: boolean;
  $align?: 'left' | 'center' | 'right';
  $appearance?: 'default' | 'minimal' | 'ghost';
}>`
  padding: var(--spacing-4) var(--spacing-4);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-700);
  text-transform: uppercase;
  font-size: var(--font-size-14);
  letter-spacing: var(--letter-spacing-normal);
  text-align: ${({ $align }) => $align || 'left'};
  white-space: no-wrap;
  // word-wrap: break-word;
  overflow-wrap: break-word;

  ${({ $appearance = 'default' }) => {
    switch ($appearance) {
      case 'minimal':
        return `border-bottom: 1px solid var(--color-gray-200);`;
      case 'ghost':
        return `border-bottom: none;`;
      case 'default':
      default:
        return `   background-color: var(--color-gray-50); border-bottom: none`;
    }
  }}

  ${({ $sortable }) =>
    $sortable &&
    `
    cursor: pointer;
    user-select: none;
    transition: var(--transition-fast);

    &:hover {
      background-color: var(--color-gray-100);
      color: var(--color-gray-900);
    }

    &:focus {
      outline: 2px solid var(--color-primary-500);
      outline-offset: -2px;
    }
  `}

  @media (max-width: 768px) {
    padding: var(--spacing-2) var(--spacing-3);
    font-size: 0.75rem;

    &:nth-child(n + 5) {
      display: none;
    }
  }
`;

// Header Content Container
export const StyledHeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-2);

  span {
    font-size: var(--font-size-16);
  }
`;

// Sort Icon
export const StyledSortIcon = styled.div<{
  $direction?: 'asc' | 'desc' | null;
}>`
  display: flex;
  align-items: center;
  font-size: var(--font-size-14);

  .sort-arrows {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0;
    line-height: 1;
  }

  .arrow-up,
  .arrow-down {
    color: var(--color-gray-600);
    transition: color 0.2s ease;
    line-height: 1;
    margin: -2px 0; /* Overlap slightly to bring them closer */
    opacity: 0.4;
  }

  .arrow-up.active {
    color: var(--color-primary-600);
    stroke-width: var(--spacing-1);
    opacity: 1;
    font-weight: bold;
  }

  .arrow-down.active {
    color: var(--color-primary-600);
    stroke-width: var(--spacing-1);
    opacity: 1;
    font-weight: bold;
  }

  /* When neither is active, show both in very muted color */
  ${({ $direction }) =>
    !$direction &&
    `
    .arrow-up,
    .arrow-down {
      color: var(--color-gray-600);
      opacity: 0.5;
    }
  `}
`;

// Filter Container
export const StyledFilterContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-4);
  margin-bottom: var(--spacing-4);
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: var(--spacing-3);
    margin: 0 -var(--spacing-2) var(--spacing-4) -var(--spacing-2);
    border-radius: 0;
    border-left: none;
    border-right: none;
  }
`;

// Search Input
export const StyledSearchInput = styled.input`
  flex: 1;
  padding: var(--spacing-2) var(--spacing-3);
  padding-left: var(--spacing-8);
  padding-right : var(--spacing-8);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  font-size: var(--font-size-14);
  background-color: var(--color-white);
  transition: var(--transition-fast);
  width: 100%;

  &:focus {
    outline: none;
    border-color: var(--color-primary-500);
    // box-shadow: 0 0 0 3px var(--color-primary-100);
  }

  &::placeholder {
    color: var(--color-gray-400);
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

// Status Filter Button
export const StyledStatusFilter = styled.button<{ $active?: boolean }>`
  padding: var(--spacing-2) var(--spacing-3);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  font-size: var(--font-size-14);
  font-weight: var(--font-weight-medium);
  background-color: ${({ $active }) =>
    $active ? 'var(--color-primary-600)' : 'var(--color-white)'};
  color: ${({ $active }) =>
    $active ? 'var(--color-white)' : 'var(--color-gray-700)'};
  cursor: pointer;
  transition: var(--transition-fast);
  white-space: nowrap;

  &:hover {
    background-color: ${({ $active }) =>
      $active ? 'var(--color-primary-700)' : 'var(--color-gray-50)'};
  }

  &:focus {
    outline: 2px solid var(--color-primary-500);
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    flex: 1;
    text-align: center;
  }
`;
